import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen therapy-bg py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 therapy-text-muted"
        >
          ← Back
        </Button>

        {/* Header */}
        <div className="therapy-bg-card border therapy-border rounded-2xl p-8 mb-6">
          <h1 className="text-4xl font-bold therapy-text mb-2">Terms of Service</h1>
          <p className="therapy-text-muted">For i am buddy</p>
          <p className="therapy-text-muted">Effective Date: February 8, 2026</p>
          <p className="therapy-text-muted">Last Updated: February 8, 2026</p>
        </div>

        {/* Content */}
        <div className="therapy-bg-card border therapy-border rounded-2xl p-8 space-y-8 therapy-text">
          {/* Critical Notice */}
          <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <span>⚠️</span>
              <span>THIS APP IS NOT THERAPY</span>
            </h2>
            <p className="mb-3">
              <strong>i am</strong> is a peer support and educational tool. It is <strong>NOT</strong>:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>❌ Medical or mental health treatment</li>
              <li>❌ Therapy, counseling, or professional healthcare</li>
              <li>❌ A substitute for professional help</li>
              <li>❌ Emergency services</li>
              <li>❌ A HIPAA-covered entity (we're not healthcare providers)</li>
            </ul>
            <p className="mt-4 font-semibold">
              If you're in crisis, call 988 (Suicide & Crisis Lifeline) or 911.
            </p>
          </div>

          {/* TL;DR */}
          <div className="bg-[var(--therapy-primary)]/10 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-4">Summary (TL;DR)</h2>
            <ul className="space-y-2">
              <li>✅ <strong>i am is</strong>: Peer support and education, not therapy</li>
              <li>❌ <strong>i am is NOT</strong>: Medical treatment, professional healthcare, or emergency services</li>
              <li>⚠️ <strong>Crisis</strong>: Call 988 or 911, not us</li>
              <li>👤 <strong>Age</strong>: 18+ only</li>
              <li>🔒 <strong>Your data</strong>: We protect it, you own it</li>
              <li>📜 <strong>Liability</strong>: We're not responsible for mental health outcomes</li>
              <li>⚖️ <strong>Disputes</strong>: Arbitration (USA) or courts (international)</li>
              <li>🚫 <strong>Bad behavior</strong>: Don't hack, harass, or abuse the system</li>
              <li>🏠 <strong>You can leave</strong>: Delete your account anytime</li>
            </ul>
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">1. What i am Is (and Isn't)</h2>
            
            <h3 className="text-xl font-semibold mb-2">i am IS:</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>✅ A <strong>peer support</strong> platform for people working on compulsive behaviors</li>
              <li>✅ <strong>Educational content</strong> based on evidence-based therapeutic approaches (ACT, DBT, CBT)</li>
              <li>✅ <strong>Self-help tools</strong> for daily check-ins, progress tracking, and crisis support</li>
              <li>✅ <strong>A supportive companion</strong> on your recovery journey</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2">i am IS NOT:</h3>
            <ul className="list-disc pl-6">
              <li>❌ <strong>Medical or mental health treatment</strong></li>
              <li>❌ <strong>Therapy, counseling, or professional healthcare</strong></li>
              <li>❌ <strong>A substitute for professional help</strong></li>
              <li>❌ <strong>Emergency services</strong></li>
              <li>❌ <strong>A HIPAA-covered entity</strong> (we're not healthcare providers)</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">2. Crisis Resources</h2>
            
            <div className="bg-[var(--therapy-primary)]/10 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-3">If You're in Crisis</h3>
              <p className="mb-4 font-semibold">i am is NOT for emergencies.</p>
              
              <p className="mb-3">If you are having thoughts of harming yourself or others, experiencing a mental health crisis, or in immediate danger:</p>
              
              {/* US */}
              <div className="mb-3">
                <p className="font-semibold mb-2">🇺🇸 United States</p>
                <div className="space-y-1 text-sm ml-4">
                  <p>🆘 <strong>988</strong> - Suicide & Crisis Lifeline (call or text)</p>
                  <p>🚨 <strong>911</strong> - Emergency services</p>
                  <p>💬 <strong>741741</strong> - Crisis Text Line (text HOME)</p>
                </div>
              </div>

              {/* UK */}
              <div className="mb-3">
                <p className="font-semibold mb-2">🇬🇧 United Kingdom</p>
                <div className="space-y-1 text-sm ml-4">
                  <p>🚨 <strong>999 or 112</strong> - Emergency services</p>
                  <p>📞 <strong>116 123</strong> - Samaritans (24/7, free)</p>
                  <p>💬 <strong>85258</strong> - Shout Crisis Text Line (text SHOUT)</p>
                  <p>🏥 <strong>111</strong> - NHS urgent mental health (press option 2)</p>
                </div>
              </div>

              {/* Australia */}
              <div className="mb-3">
                <p className="font-semibold mb-2">🇦🇺 Australia</p>
                <div className="space-y-1 text-sm ml-4">
                  <p>🚨 <strong>000</strong> - Emergency services</p>
                  <p>📞 <strong>13 11 14</strong> - Lifeline (24/7)</p>
                  <p>💙 <strong>1300 22 4636</strong> - Beyond Blue (24/7)</p>
                  <p>👤 <strong>1800 55 1800</strong> - Kids Helpline (under 25s)</p>
                </div>
              </div>

              <p className="text-sm mt-4">🌍 <strong>Other countries</strong>: <a href="https://findahelpline.com" target="_blank" rel="noopener noreferrer" className="underline">findahelpline.com</a></p>

              <p className="font-semibold text-red-700 dark:text-red-400 mt-4">
                The SOS features in this app provide coping strategies, not crisis intervention. 
                Always call professional crisis services if you're in immediate danger.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">3. Eligibility</h2>
            
            <h3 className="text-xl font-semibold mb-2">Age Requirement: 18+</h3>
            <p className="mb-3">
              You must be <strong>18 years or older</strong> to use i am.
            </p>
            
            <p className="mb-2"><strong>Why</strong>: This app deals with mature content related to compulsive behaviors, mental health challenges, and recovery. It is not designed or intended for minors.</p>
            
            <p className="mb-2">If you are under 18:</p>
            <ul className="list-disc pl-6">
              <li>Please do not create an account</li>
              <li>Seek support from a parent, guardian, school counselor, or qualified professional</li>
              <li>Resources for teens: Teen Line (1-800-852-8336), Crisis Text Line (text HOME to 741741)</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">4. Your Responsibilities</h2>
            
            <h3 className="text-xl font-semibold mb-2">You Are Responsible for Your Health</h3>
            <p className="mb-2">You should:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>✅ Continue working with your therapist, doctor, or healthcare provider</li>
              <li>✅ Follow your treatment plan as prescribed</li>
              <li>✅ Seek professional help for serious mental health concerns</li>
              <li>✅ Call emergency services (911 or 988) if you're in crisis</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2">We Cannot Guarantee Outcomes</h3>
            <p className="mb-2">
              Recovery is complex and personal. While we provide evidence-based tools:
            </p>
            <ul className="list-disc pl-6">
              <li>We cannot guarantee results</li>
              <li>We cannot predict how you will respond to the content</li>
              <li>We are not responsible for mental health outcomes</li>
              <li>Your results may vary</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">5. User Conduct</h2>
            
            <h3 className="text-xl font-semibold mb-2">You Agree NOT to:</h3>
            <ul className="list-disc pl-6">
              <li>❌ Use the app if you're under 18</li>
              <li>❌ Share your account or login credentials</li>
              <li>❌ Reverse engineer, hack, or exploit the app</li>
              <li>❌ Upload malware, viruses, or malicious code</li>
              <li>❌ Scrape, copy, or steal content or data</li>
              <li>❌ Use the app for commercial purposes without permission</li>
              <li>❌ Harass, threaten, or abuse others</li>
              <li>❌ Share illegal content or promote illegal activities</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold mb-4">6. Disclaimers and Limitations</h2>
            
            <div className="bg-amber-500/10 border-2 border-amber-500/30 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-3">No Warranties</h3>
              <p className="mb-3">THE APP IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.</p>
              
              <h3 className="text-xl font-semibold mb-3 mt-4">Limitation of Liability</h3>
              <p className="mb-2 font-semibold">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
              <p className="mb-2">WE ARE NOT LIABLE FOR:</p>
              <ul className="list-disc pl-6">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Mental health outcomes or lack of progress</li>
                <li>Decisions you make based on app content</li>
                <li>Harm resulting from use or inability to use the app</li>
              </ul>
              <p className="mt-3 font-semibold">
                OUR TOTAL LIABILITY TO YOU SHALL NOT EXCEED $100 OR THE AMOUNT YOU PAID US 
                IN THE LAST 12 MONTHS (CURRENTLY $0), WHICHEVER IS LESS.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-[var(--therapy-primary)]/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
            <div className="space-y-2">
              <p>📧 <strong>Legal questions</strong>: legal@iam-buddy.app</p>
              <p>📧 <strong>General support</strong>: support@iam-buddy.app</p>
              <p>📧 <strong>Privacy questions</strong>: privacy@iam-buddy.app</p>
              <p>📧 <strong>Security concerns</strong>: security@iam-buddy.app</p>
            </div>
          </section>

          {/* Agreement */}
          <section className="border-t-2 therapy-border pt-6">
            <h2 className="text-2xl font-bold mb-4">Your Agreement</h2>
            <p className="mb-3">By using the app, you confirm that:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>✅ You are 18 or older</li>
              <li>✅ You understand this is peer support, not therapy</li>
              <li>✅ You will seek professional help when needed</li>
              <li>✅ You understand the crisis resources and limitations</li>
              <li>✅ You've read and agree to these Terms and the Privacy Policy</li>
              <li>✅ You will use the app responsibly and legally</li>
            </ul>
          </section>

          {/* Full Terms Link */}
          <div className="text-center pt-6 border-t therapy-border">
            <p className="therapy-text-muted mb-4">
              This is a summary. For the complete Terms of Service with all legal details:
            </p>
            <Button
              onClick={() => window.open('/TERMS_OF_SERVICE.md', '_blank')}
              className="button-primary"
            >
              Read Full Terms of Service
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
